import { Text as MantineText, TextProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedTextProps extends Omit<TextProps, 'gradient'> {
  customGradient?: boolean;
  gradientVariant?: GradientVariant;
  animate?: boolean;
  children?: React.ReactNode;
  /** Polymorphic component to render as (e.g., 'a', 'span', 'p', Link) */
  component?: any;
  /** Route path when using component prop with React Router Link */
  to?: string;
  /** Standard anchor href for direct links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const Text = forwardRef<HTMLDivElement, ThemedTextProps>(
  ({ customGradient = false, gradientVariant = 'primary', animate = false, children, style, ...rest }, ref) => {
    const theme = useMantineTheme();
    const lineSpacing = usePreferencesStore((s) => s.lineSpacing);
    const letterSpacingPref = usePreferencesStore((s) => s.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    if (!customGradient) {
      return (
        <MantineText ref={ref} style={{ textAlign: 'left', ...accessibilityStyles, ...style }} {...rest}>
          {children}
        </MantineText>
      );
    }

    const gradientConfig = theme.other.gradients[gradientVariant];

    return (
      <MantineText
        ref={ref}
        component="span"
        style={{
          textAlign: 'left',
          backgroundImage: `linear-gradient(135deg, ${gradientConfig.from}, ${gradientConfig.to})`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundSize: animate ? '200% auto' : '100%',
          animation: animate ? 'gradient-shift 3s ease infinite' : undefined,
          ...accessibilityStyles,
          ...style,
        }}
        {...rest}
      >
        {children}
        {animate && (
          <style>{`
            @keyframes gradient-shift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
        )}
      </MantineText>
    );
  }
);

Text.displayName = 'Text';

export default Text;
